/**
 * 모달 팝업 관리 스크립트
 */
class ModalManager {
  constructor() {
    this.activeModal = null;
    this.modalCache = {};
    this.contentCache = {};
    this.init();
  }

  /**
   * 초기화 함수
   */
  init() {
    // 전역 이벤트 리스너 등록
    document.addEventListener('click', (e) => {
      // 모달 닫기 버튼 클릭
      if (e.target.closest('[data-dismiss="modal"]')) {
        const modal = e.target.closest('.modal-overlay');
        this.closeModal(modal.id);
      }
    });

    // ESC 키로 모달 닫기
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activeModal) {
        this.closeModal(this.activeModal.id);
      }
    });

    // 기본 모달 템플릿 로드
    this.loadBaseModal();
  }

  /**
   * 기본 모달 템플릿 로드
   */
  async loadBaseModal() {
    try {
      const response = await fetch('modals/base_modal.html');
      if (!response.ok) {
        console.warn('기본 모달 템플릿을 로드할 수 없습니다. 내장 템플릿을 사용합니다.');
        this.baseModalTemplate = `
          <div class="modal-overlay">
            <div class="modal-container">
              <div class="modal-header">
                <h3 class="modal-title"></h3>
                <button class="modal-close" data-dismiss="modal">&times;</button>
              </div>
              <div class="modal-body"></div>
              <div class="modal-footer">
                <button class="modal-btn modal-btn-cancel" data-dismiss="modal">취소</button>
                <button class="modal-btn modal-btn-primary" data-action="confirm">확인</button>
              </div>
            </div>
          </div>
        `;
        return;
      }
      
      this.baseModalTemplate = await response.text();
    } catch (error) {
      console.error('기본 모달 템플릿 로드 오류:', error);
      this.baseModalTemplate = `
        <div class="modal-overlay">
          <div class="modal-container">
            <div class="modal-header">
              <h3 class="modal-title"></h3>
              <button class="modal-close" data-dismiss="modal">&times;</button>
            </div>
            <div class="modal-body"></div>
            <div class="modal-footer">
              <button class="modal-btn modal-btn-cancel" data-dismiss="modal">취소</button>
              <button class="modal-btn modal-btn-primary" data-action="confirm">확인</button>
            </div>
          </div>
        </div>
      `;
    }
  }

  /**
   * 모달 컨텐츠 로드 함수
   * @param {string} contentId - 컨텐츠 ID
   * @returns {Promise} - 컨텐츠 로드 Promise
   */
  async loadModalContent(contentId) {
    // 이미 캐시에 있는 경우 재사용
    if (this.contentCache[contentId]) {
      return this.contentCache[contentId];
    }
    
    try {
      const response = await fetch(`modals/${contentId}.html`);
      if (!response.ok) {
        throw new Error(`모달 컨텐츠 로드 실패: ${response.status}`);
      }
      
      const html = await response.text();
      
      // 캐시에 저장
      this.contentCache[contentId] = html;
      
      return html;
    } catch (error) {
      console.error('모달 컨텐츠 로드 오류:', error);
      return null;
    }
  }

  /**
   * 모달 생성 함수
   * @param {string} modalId - 모달 ID
   * @returns {HTMLElement} - 생성된 모달 요소
   */
  createModal(modalId) {
    const modalTemplate = `
      <div class="modal-overlay" id="${modalId}">
        <div class="modal-container">
          <div class="modal-header">
            <h3 class="modal-title"></h3>
            <button class="modal-close" data-dismiss="modal">
              <i class="ri-close-line"></i>
            </button>
          </div>
          <div class="modal-body"></div>
          <div class="modal-footer">
            <button class="modal-btn modal-btn-cancel" data-dismiss="modal">취소</button>
            <button class="modal-btn modal-btn-primary" data-action="confirm">확인</button>
          </div>
        </div>
      </div>
    `;
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = modalTemplate.trim();
    const modalElement = tempDiv.firstChild;
    
    // body에 추가
    document.body.appendChild(modalElement);
    
    // 캐시에 저장
    this.modalCache[modalId] = modalElement;
    
    return modalElement;
  }

  /**
   * 모달 열기 함수
   * @param {string} modalId - 모달 ID
   * @param {Object} options - 모달 옵션
   * @returns {Promise} - 모달 열기 Promise
   */
  async openModal(modalId, options = {}) {
    // 이미 열려있는 모달이 있고, 강제로 닫지 않도록 설정된 경우가 아니면 닫기
    if (this.activeModal && !options.keepActiveModal) {
      // 현재 활성화된 모달을 비활성화만 하고 완전히 닫지는 않음
      this.activeModal.classList.remove('active');
    }
    
    // 모달 가져오기 또는 생성
    let modal = this.modalCache[modalId];
    if (!modal) {
      modal = this.createModal(modalId);
    }
    
    // 모달 제목 설정
    if (options.title) {
      const titleElement = modal.querySelector('.modal-title');
      if (titleElement) {
        titleElement.textContent = options.title;
      }
    }
    
    // 모달 컨텐츠 설정
    if (options.contentId) {
      const content = await this.loadModalContent(options.contentId);
      if (content) {
        const bodyElement = modal.querySelector('.modal-body');
        if (bodyElement) {
          bodyElement.innerHTML = content;
        }
      }
    } else if (options.content) {
      const bodyElement = modal.querySelector('.modal-body');
      if (bodyElement) {
        if (typeof options.content === 'function') {
          options.content(bodyElement);
        } else {
          bodyElement.innerHTML = options.content;
        }
      }
    }
    
    // 버튼 텍스트 설정
    if (options.cancelText) {
      const cancelBtn = modal.querySelector('[data-dismiss="modal"].modal-btn');
      if (cancelBtn) {
        cancelBtn.textContent = options.cancelText;
      }
    }
    
    if (options.confirmText) {
      const confirmBtn = modal.querySelector('[data-action="confirm"]');
      if (confirmBtn) {
        confirmBtn.textContent = options.confirmText;
      }
    }
    
    // 버튼 표시/숨김 설정
    if (options.showCancel === false) {
      const cancelBtn = modal.querySelector('[data-dismiss="modal"].modal-btn');
      if (cancelBtn) {
        cancelBtn.style.display = 'none';
      }
    }
    
    if (options.showConfirm === false) {
      const confirmBtn = modal.querySelector('[data-action="confirm"]');
      if (confirmBtn) {
        confirmBtn.style.display = 'none';
      }
    }
    
    // 확인 버튼 이벤트 설정
    if (options.onConfirm) {
      const confirmBtn = modal.querySelector('[data-action="confirm"]');
      if (confirmBtn) {
        // 기존 이벤트 리스너 제거
        const newConfirmBtn = confirmBtn.cloneNode(true);
        confirmBtn.parentNode.replaceChild(newConfirmBtn, confirmBtn);
        
        // 새 이벤트 리스너 추가
        newConfirmBtn.addEventListener('click', (e) => {
          options.onConfirm(e, modal);
          this.closeModal(modalId);
        });
      }
    }
    
    // 모달 열기
    modal.classList.add('active');
    document.body.classList.add('modal-open');
    
    // body 스크롤 잠그기 (CSS 클래스로 처리)
    // document.body.style.overflow = 'hidden'; // 이 방식 대신 CSS 클래스 사용
    
    this.activeModal = modal;
    
    // 모달 열린 후 콜백 실행
    if (options.onOpen) {
      options.onOpen(modal);
    }
    
    return modal;
  }

  /**
   * 모달 닫기 함수
   * @param {string} modalId - 모달 ID
   */
  closeModal(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return;
    
    modal.classList.remove('active');
    document.body.classList.remove('modal-open');
    
    // body 스크롤 풀기 (CSS 클래스로 처리)
    // document.body.style.overflow = 'auto'; // 이 방식 대신 CSS 클래스 사용
    
    // 현재 활성 모달이 닫히는 모달과 같은 경우
    if (this.activeModal && this.activeModal.id === modalId) {
      this.activeModal = null;
    }
  }

  /**
   * 모달 제거 함수 (DOM에서 완전히 제거)
   * @param {string} modalId - 모달 ID
   */
  removeModal(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return;
    
    if (this.activeModal && this.activeModal.id === modalId) {
      this.activeModal = null;
    }
    
    modal.remove();
    delete this.modalCache[modalId];
  }

  /**
   * 모든 모달 제거
   */
  removeAllModals() {
    Object.keys(this.modalCache).forEach(modalId => {
      this.removeModal(modalId);
    });
    
    this.modalCache = {};
    this.activeModal = null;
  }

  /**
   * 알림 모달 표시
   * @param {string} message - 메시지
   * @param {Object} options - 옵션
   */
  alert(message, options = {}) {
    // 현재 활성화된 모달 저장
    const previousActiveModal = this.activeModal;
    
    const alertOptions = {
      title: options.title || '알림',
      content: `<p>${message}</p>`,
      showCancel: false,
      confirmText: options.confirmText || '확인',
      onConfirm: (e, modal) => {
        this.closeModal(modal.id);
        
        // 이전 모달이 있었다면 다시 활성화
        if (previousActiveModal && options.keepPreviousModal !== false) {
          previousActiveModal.classList.add('active');
          this.activeModal = previousActiveModal;
        }
        
        if (options.onConfirm) options.onConfirm();
      }
    };
    
    return this.openModal('alert-modal', alertOptions);
  }

  /**
   * 확인 모달 표시
   * @param {string} message - 메시지
   * @param {Object} options - 옵션
   */
  confirm(message, options = {}) {
    const confirmOptions = {
      title: options.title || '확인',
      content: `<p>${message}</p>`,
      cancelText: options.cancelText || '취소',
      confirmText: options.confirmText || '확인',
      onConfirm: options.onConfirm
    };
    
    return this.openModal('confirm-modal', confirmOptions);
  }
}

// 전역 모달 매니저 인스턴스 생성
const modalManager = new ModalManager();

// 전역 객체에 할당
window.modalManager = modalManager; 